// ResponsiveHeader.jsx - Version avec indicateur de mises à jour des notes

import React, { useState, useEffect } from 'react';
import { 
  RefreshCw, Calendar, BarChart3, Users, Target, Zap, Globe, Settings,
  Menu, X, AlertCircle, Bell, ChevronDown, Eye, EyeOff, BookOpen,
  MessageCircle, PenTool, Database
} from 'lucide-react';

// Hook personnalisé pour la gestion de la responsivité
const useResponsive = () => {

  const [screenSize, setScreenSize] = useState('desktop');
  
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setScreenSize('mobile');
      } else if (width < 1024) {
        setScreenSize('tablet');
      } else {
        setScreenSize('desktop');
      }
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  return screenSize;
};

// 🆕 Configuration des éléments de navigation AVEC indicateur Notes
const NAVIGATION_CONFIG = [
  { id: 'chat', label: 'Chat', icon: MessageCircle, color: 'blue' },
  { id: 'planning', label: 'Planning', icon: Calendar, color: 'orange' },
  { id: 'notes', label: 'Notes', icon: BookOpen, color: 'purple', supportsUpdate: true }, // 🆕 Support d'indicateur
  { id: 'analytics', label: 'Progression', icon: BarChart3, color: 'green' },
  { id: 'social', label: 'Communauté', icon: Users, color: 'pink' }
];

// Configuration des accès rapides
const QUICK_ACCESS_CONFIG = [
  { 
    id: 'objective', 
    icon: Target, 
    label: 'Objectif', 
    color: 'blue',
    getActive: (props) => !!props.userObjective,
    getCount: () => null
  },
  { 
    id: 'actionplan', 
    icon: Zap, 
    label: 'Plan d\'action', 
    color: 'orange',
    getActive: (props) => !!props.actionPlan?.steps?.length,
    getCount: (props) => props.actionPlan?.steps?.length || 0
  },
  { 
    id: 'sources', 
    icon: Globe, 
    label: 'Sources', 
    color: 'green',
    getActive: (props) => props.savedSources?.length > 0,
    getCount: (props) => props.savedSources?.length || 0
  },
  { 
    id: 'exploration', 
    icon: Database, 
    label: 'Exploration', 
    color: 'purple',
    getActive: (props) => !!props.userObjective, // Actif si l'utilisateur a un objectif
    getCount: (props) => {
      // Format: "docs|concepts" - ex: "12|8"
      const docs = props.explorationStats?.documentsCount || 0;
      const concepts = props.explorationStats?.conceptsCount || 0;
      return docs > 0 || concepts > 0 ? `${docs}|${concepts}` : null;
    }
  }
];

// Composant pour les badges de comptage
const CountBadge = ({ count, color, size = 'default' }) => {
  if (!count || count <= 0) return null;
  
  const sizeClasses = {
    small: 'h-4 w-4 text-xs',
    default: 'h-5 w-5 text-xs',
    large: 'h-6 w-6 text-sm'
  };
  
  const colorClasses = {
    blue: 'bg-white border-2 border-blue-400 text-blue-700',
    orange: 'bg-white border-2 border-orange-400 text-orange-700',
    green: 'bg-white border-2 border-green-400 text-green-700',
    purple: 'bg-white border-2 border-purple-400 text-purple-700',
    pink: 'bg-white border-2 border-pink-400 text-pink-700',
    red: 'bg-white border-2 border-red-400 text-red-700'
  };
  
  // Vérifier si c'est un format "docs|concepts" (pour le bouton exploration)
  if (typeof count === 'string' && count.includes('|')) {
    const [docs, concepts] = count.split('|');
    const docsNum = parseInt(docs) || 0;
    const conceptsNum = parseInt(concepts) || 0;
    
    // Pour le format compact sur mobile/tablette
    if (size === 'small') {
      const displayCount = docsNum > 9 ? '9+' : docsNum;
      return (
        <span className={`absolute -top-1 -right-1 ${sizeClasses[size]} ${colorClasses[color]} rounded-full flex items-center justify-center font-semibold shadow-sm`}>
          {displayCount}
        </span>
      );
    }
    
    // Pour le format desktop avec deux badges
    return (
      <div className="absolute -top-1 -right-1 flex flex-col gap-0.5">
        {docsNum > 0 && (
          <span className={`${sizeClasses.small} ${colorClasses[color]} rounded-full flex items-center justify-center font-semibold shadow-sm`}>
            {docsNum > 9 ? '9+' : docsNum}
          </span>
        )}
        {conceptsNum > 0 && (
          <span className={`${sizeClasses.small} ${colorClasses[color]} rounded-full flex items-center justify-center font-semibold shadow-sm`}>
            {conceptsNum > 9 ? '9+' : conceptsNum}
          </span>
        )}
      </div>
    );
  }
  
  // Format normal (nombre simple)
  const displayCount = count > 99 ? '99+' : count > 9 && size === 'small' ? '9+' : count;
  
  return (
    <span className={`absolute -top-1 -right-1 ${sizeClasses[size]} ${colorClasses[color]} rounded-full flex items-center justify-center font-semibold shadow-sm`}>
      {displayCount}
    </span>
  );
};

// 🆕 NOUVEAU : Badge de mise à jour (orange pulsant)
const UpdateIndicator = ({ size = 'default' }) => {
  const sizeClasses = {
    small: 'w-2 h-2',
    default: 'w-2.5 h-2.5',
    large: 'w-3 h-3'
  };
  
  return (
    <span className="absolute -top-1 -right-1 flex">
      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75`}></span>
      <span className={`relative inline-flex rounded-full ${sizeClasses[size]} bg-orange-500 border-2 border-white`}></span>
    </span>
  );
};

// Composant pour les boutons d'accès rapide
const QuickAccessButton = ({ item, props, onClick, size = 'default' }) => {
  const isActive = item.getActive(props);
  const count = item.getCount(props);
  
  const sizeClasses = {
    small: 'p-2',
    default: 'p-2',
    large: 'p-3'
  };
  
  const iconSizes = {
    small: 16,
    default: 18,
    large: 20
  };
  
  return (
    <div className="relative">
      <button 
        onClick={() => onClick(item.id)}
        className={`${sizeClasses[size]} hover:bg-white rounded-md transition-all duration-200 ${
          isActive 
            ? `text-${item.color}-600 bg-white shadow-sm` 
            : "text-gray-400 hover:text-gray-600"
        }`}
        title={item.label}
        disabled={!isActive}
      >
        <item.icon size={iconSizes[size]} />
      </button>
      <CountBadge count={count} color={item.color} size={size === 'small' ? 'small' : 'default'} />
    </div>
  );
};

// Composant Header Desktop
const DesktopHeader = ({ 
  workspaceView, 
  onViewChange, 
  onQuickAccess, 
  onOpenModal,
  headerProps 
}) => {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16">
        
        {/* Logo compact */}
        <div className="flex items-center space-x-3 flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 flex items-center justify-center">
            <Zap className="h-6 w-6 text-blue-600" />
          </div>
          <span className="text-xl font-bold text-gray-800">Chat</span>
        </div>

        {/* Navigation principale avec indicateur Notes */}
        <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
          {NAVIGATION_CONFIG.map(item => {
            // 🆕 Vérifier si cet onglet doit afficher un indicateur de mise à jour
            const showUpdateIndicator = item.supportsUpdate && 
                                       headerProps.notesHasUpdates && 
                                       workspaceView !== item.id;
            
            return (
              <div key={item.id} className="relative">
                <button
                  onClick={() => onViewChange(item.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    workspaceView === item.id
                      ? `bg-white text-${item.color}-600 shadow-sm ring-1 ring-${item.color}-200`
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </button>
                
                {/* 🆕 Indicateur de mise à jour */}
                {showUpdateIndicator && <UpdateIndicator size="default" />}
              </div>
            );
          })}
        </div>

        {/* Actions rapides et paramètres */}
        <div className="flex items-center space-x-3">
          
          {/* Indicateur étape courante */}
          {!headerProps.currentStepId && headerProps.actionPlan?.steps?.length > 0 && (
            <div className="flex items-center space-x-2 bg-orange-50 px-3 py-1 rounded-full">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-800">
                Aucune étape
              </span>
            </div>
          )}

          {/* Accès rapide */}
          <div className="flex items-center space-x-1 bg-gray-50 rounded-lg p-1">
            {QUICK_ACCESS_CONFIG.map(item => (
              <QuickAccessButton
                key={item.id}
                item={item}
                props={headerProps}
                onClick={onQuickAccess}
                size="default"
              />
            ))}
          </div>

        </div>
      </div>
    </div>
  );
};

// Composant Header Tablette
const TabletHeader = ({ 
  workspaceView, 
  onViewChange, 
  onQuickAccess, 
  onOpenModal,
  headerProps 
}) => {
  return (
    <div className="px-4 sm:px-6">
      <div className="flex items-center justify-between h-16">
        
        {/* Logo compact */}
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 flex items-center justify-center">
            <Zap className="h-5 w-5 text-blue-600" />
          </div>
          <span className="text-lg font-bold text-gray-800">Chat</span>
        </div>

        {/* Navigation compacte avec indicateur */}
        <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
          {NAVIGATION_CONFIG.map(item => {
            const showUpdateIndicator = item.supportsUpdate && 
                                       headerProps.notesHasUpdates && 
                                       workspaceView !== item.id;
            
            return (
              <div key={item.id} className="relative">
                <button
                  onClick={() => onViewChange(item.id)}
                  className={`flex items-center justify-center p-2 rounded-md transition-all duration-200 ${
                    workspaceView === item.id
                      ? `bg-white text-${item.color}-600 shadow-sm`
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  title={item.label}
                >
                  <item.icon className="h-5 w-5" />
                </button>
                {showUpdateIndicator && <UpdateIndicator size="small" />}
              </div>
            );
          })}
        </div>

        {/* Actions compactes */}
        <div className="flex items-center space-x-2">
          {QUICK_ACCESS_CONFIG.map(item => (
            <QuickAccessButton
              key={item.id}
              item={item}
              props={headerProps}
              onClick={onQuickAccess}
              size="small"
            />
          ))}
          
          <button 
            onClick={() => onOpenModal('settings')}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors duration-200 text-gray-600"
          >
            <Settings size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Composant Header Mobile
const MobileHeader = ({ 
  workspaceView, 
  onViewChange, 
  onQuickAccess, 
  onOpenModal,
  headerProps 
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const handleViewChange = (viewId) => {
    onViewChange(viewId);
    setMobileMenuOpen(false);
  };
  
  return (
    <>
      <div className="px-4">
        <div className="flex items-center justify-between h-14">
          
          {/* Logo mobile */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 flex items-center justify-center">
              <Zap className="h-4 w-4 text-blue-600" />
            </div>
            <span className="text-base font-bold text-gray-800">Chat</span>
          </div>

          {/* Actions rapides mobile */}
          <div className="flex items-center space-x-1">
            {QUICK_ACCESS_CONFIG.map(item => (
              <QuickAccessButton
                key={item.id}
                item={item}
                props={headerProps}
                onClick={onQuickAccess}
                size="small"
              />
            ))}
            
            {/* Menu hamburger avec indicateur si notifications */}
            <div className="relative">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors duration-200 text-gray-600"
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              {headerProps.notesHasUpdates && !mobileMenuOpen && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full"></span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Menu mobile déroulant */}
      {mobileMenuOpen && (
        <div className="border-t border-gray-200 bg-white">
          
          {/* Navigation mobile */}
          <div className="px-4 py-3 space-y-1">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Navigation
            </p>
            {NAVIGATION_CONFIG.map(item => {
              const showUpdateIndicator = item.supportsUpdate && 
                                         headerProps.notesHasUpdates && 
                                         workspaceView !== item.id;
              
              return (
                <div key={item.id} className="relative">
                  <button
                    onClick={() => handleViewChange(item.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left transition-all duration-200 ${
                      workspaceView === item.id
                        ? `bg-${item.color}-50 text-${item.color}-700 border-l-4 border-${item.color}-500`
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                    {showUpdateIndicator && (
                      <span className="ml-auto">
                        <span className="flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-orange-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                        </span>
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Actions rapides mobile */}
          <div className="px-4 py-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Accès rapide
            </p>
            <div className="space-y-1">
              {QUICK_ACCESS_CONFIG.map(item => {
                const isActive = item.getActive(headerProps);
                const count = item.getCount(headerProps);
                
                return (
                  <button
                    key={item.id}
                    onClick={() => onQuickAccess(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-3 rounded-lg text-left transition-all duration-200 ${
                      isActive
                        ? `text-${item.color}-700 bg-${item.color}-50 hover:bg-${item.color}-100`
                        : 'text-gray-400 cursor-not-allowed'
                    }`}
                    disabled={!isActive}
                  >
                    <div className="flex items-center space-x-3">
                      <item.icon className="h-5 w-5" />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    {count > 0 && (
                      <span className={`px-2 py-1 bg-white border-2 border-${item.color}-400 text-${item.color}-700 text-xs rounded-full font-semibold`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Paramètres mobile */}
          <div className="px-4 py-3 border-t border-gray-100">
            <button 
              onClick={() => {
                onOpenModal('settings');
                setMobileMenuOpen(false);
              }}
              className="w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left hover:bg-gray-50 transition-colors duration-200"
            >
              <Settings className="h-5 w-5 text-gray-600" />
              <span className="font-medium text-gray-700">Paramètres</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// Composant principal ResponsiveHeader
const ResponsiveHeader = ({
  workspaceView,
  onViewChange,
  onQuickAccess,
  onOpenModal,
  // Props pour les données
  userObjective,
  actionPlan,
  savedSources,
  currentStepId,
  hasNotifications = false,
  notesHasUpdates = false, // 🆕 NOUVEAU prop pour les mises à jour de notes
  explorationStats = { documentsCount: 0, conceptsCount: 0 }, // 🆕 NOUVEAU prop pour les statistiques d'exploration
  className = ""
}) => {
  const screenSize = useResponsive();
  
  // Regrouper toutes les props nécessaires
  const headerProps = {
    userObjective,
    actionPlan,
    savedSources,
    currentStepId,
    hasNotifications,
    notesHasUpdates, // 🆕 Inclure dans headerProps
    explorationStats // 🆕 Ajouter les statistiques d'exploration
  };
  
  // Props communes pour tous les composants
  const commonProps = {
    workspaceView,
    onViewChange,
    onQuickAccess,
    onOpenModal,
    headerProps
  };
  
  return (
    <div className={`bg-white border-b border-gray-200 sticky top-0 z-50 ${className}`}>
      {screenSize === 'desktop' && <DesktopHeader {...commonProps} />}
      {screenSize === 'tablet' && <TabletHeader {...commonProps} />}
      {screenSize === 'mobile' && <MobileHeader {...commonProps} />}
      
    </div>
  );
};

export default ResponsiveHeader;
export { useResponsive, NAVIGATION_CONFIG, QUICK_ACCESS_CONFIG };
